<template>
  <div class="page-container">
    <Navbar />

    <main class="page-content max-w-4xl">
      <div class="page-header animate-fade-up">
        <Breadcrumbs :items="[{ label: 'Profile' }]" />
        <h1 class="page-title">User Profile</h1>
        <p class="page-subtitle">Manage your personal information and preferences</p>
      </div>

      <!-- Success Message -->
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 -translate-y-2">
        <div v-if="successMessage" class="mb-8 rounded-2xl border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 backdrop-blur-sm p-4 flex items-start space-x-3 animate-fade-up">
          <CheckCircleIcon class="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
          <p class="text-sm text-emerald-800 dark:text-emerald-200">{{ successMessage }}</p>
        </div>
      </transition>

      <!-- Error Message -->
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 -translate-y-2">
        <div v-if="errorMessage" class="mb-8 rounded-2xl border border-red-200/80 dark:border-red-800/60 bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm p-4 flex items-start space-x-3 animate-fade-up">
          <XCircleIcon class="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p class="text-sm text-red-800 dark:text-red-200">{{ errorMessage }}</p>
        </div>
      </transition>

      <!-- Profile Form -->
      <BaseCard class="animate-fade-up stagger-1">
        <form @submit.prevent="updateProfile" class="p-6 space-y-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- First Name -->
            <div>
              <label for="firstName" class="label">
                <UserIcon class="h-4 w-4 inline mr-1.5 text-accent-500" />
                First Name
              </label>
              <input
                id="firstName"
                v-model="profile.first_name"
                type="text"
                required
                class="input"
                placeholder="John"
              />
            </div>

            <!-- Last Name -->
            <div>
              <label for="lastName" class="label">
                <UserIcon class="h-4 w-4 inline mr-1.5 text-accent-500" />
                Last Name
              </label>
              <input
                id="lastName"
                v-model="profile.last_name"
                type="text"
                required
                class="input"
                placeholder="Doe"
              />
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="label">
                <EnvelopeIcon class="h-4 w-4 inline mr-1.5 text-accent-500" />
                Email
              </label>
              <input
                id="email"
                v-model="profile.email"
                type="email"
                disabled
                class="input bg-surface-100 dark:bg-surface-800 cursor-not-allowed opacity-60"
              />
              <p class="mt-1.5 text-xs text-surface-400 dark:text-surface-500">
                Email cannot be changed
              </p>
            </div>

            <!-- Contact Number -->
            <div>
              <label for="contact" class="label">
                <PhoneIcon class="h-4 w-4 inline mr-1.5 text-accent-500" />
                Contact Number
              </label>
              <input
                id="contact"
                v-model="profile.contact_number"
                type="tel"
                class="input"
                placeholder="+971 50 123 4567"
              />
            </div>
          </div>

          <!-- Company Logo Upload (Admin Only) -->
          <div v-if="profile.role === 'ACCOUNT_ADMIN'" class="space-y-4">
            <label class="label">
              <PhotoIcon class="h-4 w-4 inline mr-1.5 text-accent-500" />
              Company Logo
            </label>

            <div class="flex items-start space-x-6">
              <!-- Logo Preview -->
              <div class="flex-shrink-0">
                <div class="w-32 h-32 rounded-2xl overflow-hidden bg-surface-100 dark:bg-surface-800/80 flex items-center justify-center border-2 border-surface-200/80 dark:border-surface-700/60 transition-colors duration-200">
                  <img
                    v-if="logoPreview"
                    :src="logoPreview"
                    alt="Company Logo"
                    class="w-full h-full object-contain"
                  />
                  <PhotoIcon v-else class="h-12 w-12 text-surface-400 dark:text-surface-600" />
                </div>
              </div>

              <!-- Upload Button -->
              <div class="flex-1">
                <label
                  for="logo-upload"
                  class="cursor-pointer inline-flex items-center px-5 py-2.5 border border-surface-200/80 dark:border-surface-700/60 rounded-xl text-sm font-medium text-surface-700 dark:text-surface-200 bg-white dark:bg-surface-800/80 hover:bg-surface-50 dark:hover:bg-surface-700/80 hover:border-accent-300 dark:hover:border-accent-600/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition-all duration-200 shadow-sm"
                >
                  <ArrowUpTrayIcon class="h-5 w-5 mr-2 text-accent-500" />
                  Choose Logo
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  @change="onImageChange"
                  class="hidden"
                />
                <p class="mt-2.5 text-xs text-surface-400 dark:text-surface-500">
                  PNG, JPG or WEBP up to 2MB
                </p>
                <p v-if="imageFile" class="mt-2 text-sm font-medium text-accent-600 dark:text-accent-400">
                  Selected: <span class="font-mono">{{ imageFile.name }}</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end space-x-3 pt-8 border-t border-surface-200/60 dark:border-surface-800/60">
            <BaseButton
              type="button"
              variant="secondary"
              @click="$router.push('/dashboard')"
            >
              Cancel
            </BaseButton>
            <BaseButton
              type="submit"
              variant="primary"
              :loading="loading"
              :icon="loading ? undefined : CheckIcon"
            >
              {{ loading ? 'Saving...' : 'Save Changes' }}
            </BaseButton>
          </div>
        </form>
      </BaseCard>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { db, storage } from '@/firebase/config'
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage'
import { doc, updateDoc, setDoc } from 'firebase/firestore'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'

import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const router = useRouter()

const userProfile = computed(() => store.state.userProfile)
const user = computed(() => store.state.user)

const loading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const profile = ref({
  first_name: '',
  last_name: '',
  email: '',
  contact_number: '',
  customer_logo: null,
  role: '',
  primary_account: '',
})

const imageFile = ref(null)
const logoPreview = ref('')

const onImageChange = (event) => {
  const file = event.target.files[0]
  if (file) {
    if (file.size > 2 * 1024 * 1024) {
      errorMessage.value = 'Image size must be less than 2MB'
      return
    }

    imageFile.value = file
    const reader = new FileReader()
    reader.onload = (e) => {
      logoPreview.value = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

watchEffect(() => {
  if (userProfile.value) {
    profile.value = { ...userProfile.value }
    if (profile.value.customer_logo && profile.value.customer_logo !== 'URL') {
      logoPreview.value = profile.value.customer_logo
    }
  }
})

const updateProfile = async () => {
  loading.value = true
  successMessage.value = ''
  errorMessage.value = ''

  try {
    // Upload image if selected
    if (imageFile.value) {
      const fileRef = storageRef(
        storage,
        `company-logos/${profile.value.primary_account}/logo.jpg`
      )
      const uploadTask = uploadBytesResumable(fileRef, imageFile.value)

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          () => resolve()
        )
      })

      profile.value.customer_logo = await getDownloadURL(fileRef)
    }

    // Update Firestore
    const docRef = doc(db, 'Users', user.value.uid)
    await updateDoc(docRef, profile.value)

    // Update logo in separate collection if changed
    if (profile.value.customer_logo && imageFile.value) {
      const primaryAccountRef = doc(db, 'Logos', profile.value.primary_account)
      await setDoc(primaryAccountRef, {
        url: profile.value.customer_logo,
      })
    }

    successMessage.value = 'Profile updated successfully!'
    imageFile.value = null

    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
  } catch (error) {
    console.error('Error updating profile:', error)
    errorMessage.value = 'An error occurred while updating your profile. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>
